import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';

import { ConfigService } from '@finder/core/config.service';
import { AI_SEARCH_SERVICE } from '@finder/features/ai-search/ai-search.constants';
import { AiMenuProperties } from '@finder/features/ai-search/components/ai-menu-modal/ai-menu-modal.interface';
import { AiSearchFeedbackOptions, AiSearchSelectionOptions } from '@finder/features/ai-search/services/ai-search/ai-service.interface';
import { GeolocationService } from '@finder/features/ai-search/services/geolocation/geolocation.service';
import { CONFIG_CONSTANTS } from '@finder/shared/constants/app.constants';
import { LocalStorageService } from '@finder/shared/services/local-storage/local-storage.service';
import getApiBase from '@finder/shared/utils/string/getApiBase';
import { Observable, Subject, of, takeUntil } from 'rxjs';
import { isEmpty } from 'lodash-es';

@Injectable({
    providedIn: 'root'
})
export class AiSearchService implements OnDestroy {

    private propertiesUpdated$ = new Subject<AiMenuProperties>();
    get propertiesUpdated() {
        return this.propertiesUpdated$.asObservable();
    }

    get apiBase() {
        const base = this.config.getValue(CONFIG_CONSTANTS.apiUrlKey);
        const version = this.config.getValue(CONFIG_CONSTANTS.defaultVersionKey);
        const siteId = this.config.getSiteId();

        return `${getApiBase(base, version)}/ai-search/${siteId}`;
    }

    private userType = AI_SEARCH_SERVICE.guest;
    private swid: string;
    private vertexDirectEnabled = false;
    private engineId: string;
    private clientId: string;
    private prompt: string;
    private rephrase: string;
    private filter: string;
    private decayConfig: string;
    private summaryDisabled = false;
    private coords: GeolocationCoordinates;
    private selectedLocation: string;
    private useCoords = false;

    private destroy$ = new Subject<void>();

    constructor(
        private http: HttpClient,
        private config: ConfigService,
        private localStorageService: LocalStorageService,
        private geolocationService: GeolocationService,
    ) { }

    /* istanbul ignore next */
    ngOnDestroy() {
        // Ignore the if just for coverage, there's no need to test unsubscribe
        this.destroy$.next();
        this.destroy$.complete();
    }

    init(): Observable<any>  {
        const url = `${this.apiBase}/search`;

        return this.http.get(url);
    }

    start(searchQuery: string): Observable<any>  {
        const siteId = this.config.getSiteId();
        const params: any = {
            siteId,
            sessionId: '',
            userType: this.userType,
            swid: this.swid,
        };

        if (searchQuery) {
            params.queryString = searchQuery;
        }

        this.setAdditionalParams(params);

        const url = `${this.apiBase}/search`;

        return this.http.post(url, { params });
    }

    followUp(searchQuery: string, sessionId: string): Observable<any>  {
        const siteId = this.config.getSiteId();
        const params: any = {
            siteId,
            sessionId,
            userType: this.userType,
            swid: this.swid,
        };

        if (searchQuery) {
            params.queryString = searchQuery;
        }

        this.setAdditionalParams(params);

        const url = `${this.apiBase}/search`;

        return this.http.post(url, { params });
    }

    setUserType(ut) {
        this.userType = ut;

        // Store in local storage
        this.localStorageService.set(AI_SEARCH_SERVICE.userType, ut);

        // Send update
        this.propertiesUpdated$.next(this.getMenuProperties());
    }

    setSwid(swid) {
        this.swid = swid;

        // Store in local storage
        this.localStorageService.set(AI_SEARCH_SERVICE.swid, swid);

        // Send update
        this.propertiesUpdated$.next(this.getMenuProperties());
    }

    setVertexDirectEnabled(value) {
        this.vertexDirectEnabled = value;

        // Store in local storage
        this.localStorageService.set(AI_SEARCH_SERVICE.vertexDirectEnabled, value);

        // Send update
        this.propertiesUpdated$.next(this.getMenuProperties());
    }

    setEngineId(value) {
        this.engineId = value;

        // Store in local storage
        this.localStorageService.set(AI_SEARCH_SERVICE.engineId, value);

        // Send update
        this.propertiesUpdated$.next(this.getMenuProperties());
    }

    setClientId(value) {
        this.clientId = value;

        // Store in local storage
        this.localStorageService.set(AI_SEARCH_SERVICE.clientId, value);

        // Send update
        this.propertiesUpdated$.next(this.getMenuProperties());
    }

    setPrompt(value) {
        this.prompt = value;

        // Store in local storage
        this.localStorageService.set(AI_SEARCH_SERVICE.prompt, value);

        // Send update
        this.propertiesUpdated$.next(this.getMenuProperties());
    }

    setRephrase(value) {
        this.rephrase = value;

        // Store in local storage
        this.localStorageService.set(AI_SEARCH_SERVICE.rephrase, value);

        // Send update
        this.propertiesUpdated$.next(this.getMenuProperties());
    }

    setFilter(value) {
        this.filter = value;

        // Update local storage
        if (isEmpty(value)) {
            this.localStorageService.remove(AI_SEARCH_SERVICE.filter);
        } else {
            this.localStorageService.set(AI_SEARCH_SERVICE.filter, value);
        }
        // Send update
        this.propertiesUpdated$.next(this.getMenuProperties());
    }

    setDecay(value) {
        this.decayConfig = value;

        // Update local storage
        if (isEmpty(value)) {
            this.localStorageService.remove(AI_SEARCH_SERVICE.decayConfig);
        } else {
            this.localStorageService.set(AI_SEARCH_SERVICE.decayConfig, value);
        }

        // Send update
        this.propertiesUpdated$.next(this.getMenuProperties());
    }

    setSummaryDisabled(value) {
        this.summaryDisabled = value;

        // Store in local storage
        this.localStorageService.set(AI_SEARCH_SERVICE.summaryDisabled, value);

        // Send update
        this.propertiesUpdated$.next(this.getMenuProperties());
    }

    setGeolocation(selectedLocation: string, coords: GeolocationCoordinates | any) {
        this.selectedLocation = selectedLocation;
        this.coords = coords;

        // Store in local storage
        this.localStorageService.set(AI_SEARCH_SERVICE.selectedLocation, selectedLocation);
        this.localStorageService.set(AI_SEARCH_SERVICE.coords, coords);

        // Send update
        this.propertiesUpdated$.next(this.getMenuProperties());
    }

    setUseCoords(value) {
        this.useCoords = value;

        // Store in local storage
        this.localStorageService.set(AI_SEARCH_SERVICE.useCoords, value);

        // Send update
        this.propertiesUpdated$.next(this.getMenuProperties());
    }

    async resetCoords() {
        // Reset location
        this.selectedLocation = void 0;

        // Get coords
        await this.getCoords();

        // Update local storage
        this.localStorageService.remove(AI_SEARCH_SERVICE.selectedLocation);
        this.localStorageService.set(AI_SEARCH_SERVICE.coords, this.coords);

        // Send update
        this.propertiesUpdated$.next(this.getMenuProperties());
    }

    getMenuProperties(): AiMenuProperties {
        // Return values
        return {
            userType: this.userType,
            swid: this.swid ?? '',
            vertexDirectEnabled: this.vertexDirectEnabled,
            engineId: this.engineId,
            clientId: this.clientId,
            prompt: this.prompt,
            rephrase: this.rephrase,
            summaryDisabled: this.summaryDisabled,
            coords: this.coords,
            selectedLocation: this.selectedLocation,
            siteId: this.config.getValue('siteId'),
            useCoords: this.useCoords,
            filter: this.filter,
            decayConfig: this.decayConfig,
        };
    }

    async initProperties() {
        await this.getCoords();

        // Read from local storage
        Object.keys(AI_SEARCH_SERVICE).forEach(key => {
            const value = this.localStorageService.get(key);
            if (value) {
                this[key] = value;
            }
        });
    }

    async getCoords() {
        // Get geo
        await new Promise((resolve, reject) => {
            this.geolocationService.getLocation()
            .pipe(takeUntil(this.destroy$))
            .subscribe(coords =>{
                this.coords = coords;

                resolve(true);
            });
        });
    }

    /**
     * POST {{ss_url}}/v1/ai-analytics/feedback
    {
        "brand": "wdw",
        "sessionId": "1797ca95-91c9-4e2e-b1bd-9c38e6f386a9",
        "userId": "{5f26f01a-bbee-4202-9298-81261067abbd}",
        "userType": "guest",
        "clientType": "web",
        "type": "AI_OVERVIEW",
        "feedback": 1
    }
     */
    feedback(searchId: string, sessionId: string, options: AiSearchFeedbackOptions) {
        const siteId = this.config.getSiteId();
        const params: any = {
            siteId,
            sessionId,
            userId: this.swid,
            userType: this.userType,
            clientType: AI_SEARCH_SERVICE.clientType,
            type: options.type,
            feedback: options.feedback,
            searchId,
        };

        const url = `${this.apiBase}/feedback`;

        return this.http.post(url, { params });
    }

    /**
     * POST {{ss_url}}/v1/ai-analytics/selections
    {
        "brand": "wdw",
        "sessionId": "1797ca95-91c9-4e2e-b1bd-9c38e6f386a9",
        "userId": "{5f26f01a-bbee-4202-9298-81261067abbd}",
        "userType": "guest",
        "searchId": "1326f01a-bbee-4202-9298-81261067bdda",
        "searchString": "search term",
        "clientType": "web",
        "documentId": "18692523;entityType=restaurant",
        "title": "EPCOT International Festival of the Holidays presented by AdventHealth - Holiday Kitchens",
        "position": 2,
        "url": "https://latest.disneyworld.disney.go.com/dining/disney-springs/polite-pig/",
        "selectionLocation": "RESULTS_LIST"
    }
     */
    selections(searchId: string, sessionId: string, options: AiSearchSelectionOptions) {
        const siteId = this.config.getSiteId();
        const params: any = {
            siteId,
            sessionId,
            userId: this.swid,
            userType: this.userType,
            searchId,
            clientType: AI_SEARCH_SERVICE.clientType,
        };

        // documentId: options.entityId,
        // title: options.title,
        // position: options.position,
        // url: options.url,
        // selectionLocation: options.location,
        // searchString: options.searchString,
        Object.entries(options).forEach(([key, value]) => {
            params[key] = value;
        });

        const url = `${this.apiBase}/selections`;

        fetch(url, {
            method: 'POST',
            body: JSON.stringify({ params }),
            keepalive: true
        });
    }

    private setAdditionalParams(params) {
        if (this.vertexDirectEnabled) {
            params.searchType = AI_SEARCH_SERVICE.searchType.vertexDirect;
        }

        if (this.engineId) {
            params.engineId = this.engineId;
        }

        if (this.clientId) {
            params.clientId = this.clientId;
        }

        if (this.prompt) {
            params.prompt = this.prompt;
        }

        if (this.rephrase) {
            params.rephrase = this.rephrase;
        }

        if (this.filter) {
            params.filter = this.filter;
        }

        if (this.decayConfig) {
            params.decayConfig = this.decayConfig;
        }

        if (this.coords && this.useCoords) {
            params.coords = this.coords;
        }
    }
}
